import type { PatientTimelineEventKind } from '@zero-dash/types';
import type {
  ChartAssay,
  IAssayFilterState,
  IBiomarkerFetchState,
  IDiseaseBurdenFetchState,
  IPopupColumn,
  ISampleTrack,
  ITrackOrderState,
  ITrackRowDefinition,
} from '@/types/PatientDashboard/PatientTimeline.types';
import { corePalette, timelineColours } from '@/themes/colours';
import { reportOptions } from '@/constants/Reports/reports';

export const LABEL_WIDTH = 200;
export const ROW_HEIGHT = 32;
export const DAY_MS = 86400000;
export const TRACK_GRID_COLUMNS = 60;
export const TRACK_GRID_BACKGROUND = {
  backgroundColor: corePalette.grey10,
  backgroundImage: `linear-gradient(to right, ${corePalette.white} 0, ${corePalette.white} 1px, transparent 1px)`,
  backgroundSize: `calc(100% / ${TRACK_GRID_COLUMNS}) 100%`,
} as const;
export const MARKER_DOT_WIDTH = 8;
export const MARKER_LABEL_GAP = 6;
export const MARKER_LABEL_MIN_WIDTH = 28;
export const MARKER_LABEL_MAX_WIDTH = 220;
export const FALLBACK_MIN_VIEW_WIDTH = 0.04;
export const DEFAULT_MAX_TICK_COLUMNS = 7;
export const MIN_VIEW_DAY_SPAN = DEFAULT_MAX_TICK_COLUMNS - 1;
export const MONTH_TICK_MIN_WIDTH = 78;
export const TIMELINE_VIEW_MARGIN_PCT = 2;
export const DESIGN_BORDER = timelineColours.designBorder;
export const HOVER_BACKGROUND = corePalette.grey10;
export const TIMELINE_COLOURS = {
  accentGreen: timelineColours.accentGreen,
  neutralBorder: timelineColours.neutralBorder,
  labelMuted: timelineColours.labelMuted,
  iconMuted: timelineColours.iconMuted,
  tickLine: timelineColours.tickLine,
  idLabel: timelineColours.idLabel,
  idValue: timelineColours.idValue,
  crosshairLine: corePalette.offBlack100,
  sampleDot: timelineColours.sampleDot,
  dragHandle: timelineColours.dragHandle,
  dropHighlight: timelineColours.dropHighlight,
} as const;

export const VISIBLE_EVENT_KINDS: PatientTimelineEventKind[] = ['Diagnosis', 'Relapse', 'Progression', 'Death'];

export const REPORT_TRACKS: ITrackRowDefinition[] = reportOptions.map((option) => ({
  id: option.value,
  label: option.name,
}));

export const SAMPLE_TRACKS: Pick<ISampleTrack, 'id' | 'label'>[] = [
  { id: 'tumour', label: 'Tumour (TT)' },
  { id: 'lb', label: 'Liquid Biopsies (LB)' },
  { id: 'germline', label: 'Germline (GP)' },
  { id: 'unknown', label: 'Unknown (UN)' },
];

export const DEFAULT_TRACK_ORDER: ITrackOrderState = {
  samples: SAMPLE_TRACKS.map((track) => track.id),
  reports: REPORT_TRACKS.map((track) => track.id),
};

// The treatment lanes are not built yet, so the section stays hidden until they are.
export const IS_TREATMENTS_SECTION_VISIBLE = false;

export const CHART_LANE_MIN_ROW_HEIGHT = 200;
export const CHART_LANE_Y_AXIS_WIDTH = 40;
export const CHART_LANE_Y_PADDING_PCT = 12;

export const CHART_LANE_LOG = {
  ladder: [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10],
  maxPct: 100,
  minDecades: 2,
  minTickGapPct: 8,
} as const;

export const CHART_LANE_COLOURS = {
  gridLine: corePalette.grey30,
  axisLabel: corePalette.grey100,
  toggleOn: TIMELINE_COLOURS.accentGreen,
} as const;

export const CHART_LANE_CONTROL_ICON_SIZE = 16;

export const CHART_LANE_LABEL = {
  minGapPct: 8,
  shiftPx: 44,
} as const;

export const CHART_LANE_LINE_STROKE_WIDTH = 2.5;

export const CHART_LABEL_PLACEMENT = {
  ABOVE: 'above',
  BELOW: 'below',
} as const;

export const CHART_LANE_SERIES_COLOURS: string[] = [
  corePalette.orange150,
  corePalette.turquoise100,
  corePalette.violet100,
  corePalette.warmRed100,
  corePalette.green150,
  corePalette.blue150,
  corePalette.magenta150,
  corePalette.yellow200,
  corePalette.purple150,
  corePalette.blue300,
  corePalette.turquoise200,
  corePalette.green300,
  corePalette.orange300,
  corePalette.magenta300,
];

export const BIOMARKER_DOT_RADIUS = 3.5;

export const UNKNOWN_MANIFEST_ID = 0;

export const BIOMARKER_FETCH_CONCURRENCY = 6;

export const BIOMARKER_LOADING_STATE: IBiomarkerFetchState = {
  snvs: [],
  isLoading: true,
  hasFailure: false,
};

const DESIGN_PINNED_ANALYTE_COLOURS: string[] = [
  corePalette.warmRed100,
  corePalette.violet100,
];

export const ANALYTE_COLOURS: string[] = [
  ...DESIGN_PINNED_ANALYTE_COLOURS,
  ...CHART_LANE_SERIES_COLOURS.filter(
    (colour) => !DESIGN_PINNED_ANALYTE_COLOURS.includes(colour),
  ),
];

export const UNKNOWN_ANALYTE = 'Unknown';
export const UNKNOWN_ASSAY: ChartAssay = 'Unknown';

export const DISEASE_BURDEN_MARKER_SIZE = 12;

export const DISEASE_BURDEN_LOD = {
  dashArray: '3 3',
  colour: corePalette.grey100,
  hoverColour: corePalette.grey200,
  strokeWidth: 2,
} as const;

export const CHART_LANE_DIMMED_OPACITY = 0.25;

export const INFORMED_MODE = {
  INFORMED: 'informed',
  UNINFORMED: 'uninformed',
} as const;

export const DEFAULT_ASSAY_FILTER_STATE: IAssayFilterState = {
  isSelected: true,
  mode: INFORMED_MODE.INFORMED,
};

export const WISP_BIOSAMPLE_ID_BATCH_SIZE = 20;

export const DISEASE_BURDEN_LOADING_STATE: IDiseaseBurdenFetchState = {
  results: [],
  isLoading: true,
  hasFailure: false,
};

export const HOVER_CLOSE_DELAY_MS = 180;
export const TIMELINE_POPUP_WIDTH = '320px';
export const POPUP_BLOCK_PADDING = '8px 12px';
export const POPUP_TEXT_SIZE = '14px';
export const POPUP_GLYPH_INDENT = '14px';
export const POPUP_MAX_HEIGHT = 'calc(100vh - 16px)';
export const POPUP_SAMPLES_MAX_HEIGHT = '240px';
export const POPUP_SAMPLE_CHILD_LIMIT = 2;

export const MONITORING_LABEL = 'Monitoring';
export const MONITORING_TOOLTIP = 'Zoom to the range where biosamples were collected';
export const MONITORING_VIEW_PADDING_DAYS = 14;

export const POPUP_BURDEN_COLUMNS: IPopupColumn[] = [
  { key: 'purity', label: 'TF%', width: '48px' },
  { key: 'lod', label: 'LOD%', width: '48px' },
];

export const POPUP_BIOMARKER_COLUMNS: IPopupColumn[] = [
  { key: 'vaf', label: 'VAF%', width: '56px' },
  { key: 'refad', label: 'REF', width: '32px' },
  { key: 'altad', label: 'ALT', width: '32px' },
  { key: 'depth', label: 'DP', width: '32px' },
];

export const DISEASE_BURDEN_VALUE_COLOURS = {
  detected: corePalette.warmRed100,
  notDetected: corePalette.green150,
} as const;
